import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

/**
 * AuthorizedButton renders a standard Button if the current user has the required permission or role.
 * If unauthorized, it renders a greyed-out disabled button that displays a tooltip on hover:
 * "You don't have access to perform this action".
 */
export const AuthorizedButton = ({
  permission,
  anyPermissions = [],
  allPermissions = [],
  allowedRoles = [],
  tooltipMessage = "You don't have access to perform this action",
  className,
  onClick,
  disabled: propDisabled = false,
  children,
  ...props
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, hasRole } = useAuth();

  let isAuthorized = true;

  if (permission && !hasPermission(permission)) {
    isAuthorized = false;
  }

  if (isAuthorized && Array.isArray(anyPermissions) && anyPermissions.length > 0 && !hasAnyPermission(anyPermissions)) {
    isAuthorized = false;
  }

  if (isAuthorized && Array.isArray(allPermissions) && allPermissions.length > 0 && !hasAllPermissions(allPermissions)) {
    isAuthorized = false;
  }

  // Fallback to role check if no permissions were passed and allowedRoles is specified
  if (
    isAuthorized &&
    !permission &&
    anyPermissions.length === 0 &&
    allPermissions.length === 0 &&
    allowedRoles.length > 0 &&
    !hasRole(allowedRoles)
  ) {
    isAuthorized = false;
  }

  if (!isAuthorized) {
    return (
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-block cursor-not-allowed">
              <Button
                {...props}
                type="button"
                disabled
                tabIndex={-1}
                className={cn(
                  'opacity-60 cursor-not-allowed pointer-events-none bg-slate-200 text-slate-500 hover:bg-slate-200 hover:text-slate-500 border-slate-300 shadow-none',
                  className
                )}
              >
                {children}
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="bg-slate-900 text-white text-xs px-2.5 py-1.5 shadow-md">
            <p className="flex items-center gap-1.5 font-medium">
              <span className="text-amber-400">🔒</span> {tooltipMessage}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Button
      {...props}
      disabled={propDisabled}
      onClick={onClick}
      className={className}
    >
      {children}
    </Button>
  );
};

export default AuthorizedButton;


